// src/hooks/websocket/useWebSocketConnection.ts
import { useCallback, useRef, useEffect } from 'react';
import { WebSocketState, WebSocketStatus, WebSocketConfig } from './types';
import { toast } from 'react-hot-toast';

interface UseWebSocketConnectionProps {
  accessToken: string | null;
  updateState: (updates: Partial<WebSocketState>) => void;
  handleMessage: (event: MessageEvent) => void;
  config: WebSocketConfig;
}

export const useWebSocketConnection = ({
  accessToken,
  updateState,
  handleMessage,
  config
}: UseWebSocketConnectionProps) => {
  const wsRef = useRef<WebSocket | null>(null);
  const isConnectingRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldReconnectRef = useRef(true);
  const connectionAttemptsRef = useRef(0);

  const cleanup = useCallback(() => {
    shouldReconnectRef.current = false;
    
    if (wsRef.current) {
      // Remove all event listeners
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      
      // Close connection if open
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, 'Normal closure');
      }
    }
    
    // Clear any pending timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Reset flags
    isConnectingRef.current = false;
    wsRef.current = null;
    connectionAttemptsRef.current = 0;
  }, []);

  const handleReconnect = useCallback((state: WebSocketState) => {
    // Don't attempt to reconnect if we shouldn't
    if (!shouldReconnectRef.current) {
      return;
    }

    const now = Date.now();
    const timeSinceLastReconnect = now - state.lastReconnectTime;

    // Prevent reconnection if we're already connecting or trying too soon
    if (isConnectingRef.current || timeSinceLastReconnect < config.MIN_TIME_BETWEEN_RECONNECTS) {
      return;
    }

    connectionAttemptsRef.current += 1;

    // Check if we've exceeded max attempts
    if (connectionAttemptsRef.current > config.MAX_RECONNECT_ATTEMPTS) {
      shouldReconnectRef.current = false;
      updateState({ 
        status: WebSocketStatus.CLOSED,
        error: new Error('Max reconnection attempts reached')
      });
      toast.error("Unable to establish real-time connection. Please refresh the page.", {
        duration: 5000,
      });
      return;
    }

    updateState({ 
      status: WebSocketStatus.RECONNECTING,
      reconnectAttempt: connectionAttemptsRef.current,
      lastReconnectTime: now
    });

    const delay = Math.min(
      config.RECONNECT_DELAY * Math.pow(2, connectionAttemptsRef.current - 1),
      10000
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      if (shouldReconnectRef.current) {
        initializeWebSocket();
      }
    }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, updateState]);

  const initializeWebSocket = useCallback(() => {
    // Don't initialize if conditions aren't met
    if (!accessToken || 
        !shouldReconnectRef.current || 
        isConnectingRef.current || 
        wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    isConnectingRef.current = true;

    try {
      const wsUrl = `${process.env.REACT_APP_WS_URL}/ws?token=${accessToken}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        connectionAttemptsRef.current = 0;
        updateState({ 
          status: WebSocketStatus.OPEN,
          error: null,
          reconnectAttempt: 0,
          lastReconnectTime: Date.now()
        });
      };

      ws.onmessage = handleMessage;

      ws.onclose = (event) => {
        isConnectingRef.current = false;
        
        // Only attempt reconnection for abnormal closures and when we should reconnect
        if (event.code !== 1000 && shouldReconnectRef.current) {
          const newState = { 
            status: WebSocketStatus.CLOSED,
            lastReconnectTime: Date.now()
          };
          updateState(newState);
          handleReconnect(newState as WebSocketState);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        isConnectingRef.current = false;
        if (shouldReconnectRef.current) {
          updateState({ status: WebSocketStatus.CLOSED });
        }
      };

    } catch (error) {
      console.error('WebSocket initialization error:', error);
      isConnectingRef.current = false;
    }
  }, [accessToken, handleMessage, updateState, handleReconnect]);

  // Initialize connection
  useEffect(() => {
    shouldReconnectRef.current = true;
    if (accessToken) {
      initializeWebSocket();
    }
    
    return () => {
      cleanup();
    };
  }, [accessToken, initializeWebSocket, cleanup]);

  return {
    wsRef,
    initializeWebSocket,
    cleanup
  };
};